/**
 * What's New — the full release-highlights history at /whats-new.
 *
 * Renders the same curated source as the one-time WhatsNewDialog
 * (src/data/whatsNew.ts). The popover shows only what's new since your last
 * visit; this page is the complete list.
 */

import { makeStyles, tokens, Title2, Caption1, Badge } from '@fluentui/react-components'
import { Sparkle24Regular } from '@fluentui/react-icons'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { CURRENT_VERSION, shippedReleases } from '../data/whatsNew'

// Newest-first, so the first entry is the latest highlighted release. Unpinned
// notes are excluded: on a branch a new note has no version yet, and rendering
// one would put a section literally headed "NEXT" on the page — and badge it as
// the latest release, above the version people are actually running.
const SHIPPED = shippedReleases()
const LATEST_VERSION = SHIPPED[0]?.version

const useStyles = makeStyles({
  root: {
    maxWidth: '760px',
    margin: '0 auto',
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalL}`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  sub: { color: tokens.colorNeutralForeground3 },
  release: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  verRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  ver: {
    margin: 0,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  list: {
    margin: 0,
    paddingLeft: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    color: tokens.colorNeutralForeground2,
    lineHeight: 1.6,
  },
})

export function WhatsNew() {
  const styles = useStyles()
  useDocumentTitle('What’s new')

  return (
    <div className={styles.root} data-testid="whats-new-page">
      <div className={styles.header}>
        <Sparkle24Regular style={{ color: '#1f5bb5' }} aria-hidden />
        <Title2>What’s new</Title2>
      </div>
      <Caption1 className={styles.sub}>
        You’re on v{CURRENT_VERSION}.
      </Caption1>

      {SHIPPED.map((r) => (
        <section
          key={r.version}
          className={styles.release}
          aria-label={`Version ${r.version}`}
          data-testid={`whats-new-entry-${r.version}`}
        >
          <div className={styles.verRow}>
            <h2 className={styles.ver}>v{r.version}</h2>
            {r.version === LATEST_VERSION && (
              <Badge appearance="tint" color="brand">
                Latest
              </Badge>
            )}
            <Caption1 className={styles.sub}>{r.date}</Caption1>
          </div>
          <ul className={styles.list}>
            {r.highlights.map((h, i) => (
              <li key={i}>{h}</li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}
